export type OwnerSystemCode =
    | 'hrm'
    | 'crm'
    | 'loyalty'
    | 'clinics'
    | 'doctors_clinics'
    | 'education'
    | 'gyms'
    | 'printeries';

export type OwnerSystemSection = 'packages' | 'control' | 'subscriptions' | 'subscribers';

/** Package `types` values used by stand-alone subscriptions API. */
export type PackageTypeCode = OwnerSystemCode | 'clinico' | 'normal';

export type OwnerSystemDef = {
    code: OwnerSystemCode;
    nameKey: string;
    /** Stored on subscription packages.types */
    packageLine: PackageTypeCode;
    launchUrl: string;
    accent: string;
    repoHint: string;
};

export const OWNER_SYSTEMS: OwnerSystemDef[] = [
    {
        code: 'hrm',
        nameKey: 'controlPlane.systemHrm',
        packageLine: 'hrm',
        launchUrl: 'https://hrm.mrnware.com',
        accent: '#0F766E',
        repoHint: 'hrm',
    },
    {
        code: 'crm',
        nameKey: 'controlPlane.systemCrm',
        packageLine: 'crm',
        launchUrl: 'https://crm.mrnware.com',
        accent: '#0369A1',
        repoHint: 'mrn-crm',
    },
    {
        code: 'loyalty',
        nameKey: 'controlPlane.systemLoyalty',
        packageLine: 'loyalty',
        launchUrl: 'https://loyalty.mrnware.com',
        accent: '#BE185D',
        repoHint: 'loyality-system',
    },
    {
        code: 'clinics',
        nameKey: 'controlPlane.systemClinics',
        packageLine: 'clinics',
        launchUrl: 'https://clinics.mrnware.com',
        accent: '#1D4ED8',
        repoHint: 'mrn-education/apps/clinics',
    },
    {
        code: 'doctors_clinics',
        nameKey: 'controlPlane.systemDoctorsClinics',
        packageLine: 'doctors_clinics',
        launchUrl: 'https://doctors.mrnware.com',
        accent: '#4338CA',
        repoHint: 'mrn-education/apps/doctors-clinics',
    },
    {
        code: 'education',
        nameKey: 'controlPlane.systemEducation',
        packageLine: 'education',
        launchUrl: 'https://education.mrnware.com',
        accent: '#7C3AED',
        repoHint: 'mrn-education/apps/education',
    },
    {
        code: 'gyms',
        nameKey: 'controlPlane.systemGyms',
        packageLine: 'gyms',
        launchUrl: 'https://gyms.mrnware.com',
        accent: '#B45309',
        repoHint: 'mrn-education/apps/gyms',
    },
    {
        code: 'printeries',
        nameKey: 'controlPlane.systemPrinteries',
        packageLine: 'printeries',
        launchUrl: 'https://printeries.mrnware.com',
        accent: '#334155',
        repoHint: 'mrn-education/apps/printeries',
    },
];

export const OWNER_SYSTEM_SECTIONS: { id: OwnerSystemSection; nameKey: string }[] = [
    { id: 'packages', nameKey: 'controlPlane.sectionPackages' },
    { id: 'control', nameKey: 'controlPlane.sectionControl' },
    { id: 'subscriptions', nameKey: 'controlPlane.sectionSubscriptions' },
    { id: 'subscribers', nameKey: 'controlPlane.sectionSubscribers' },
];

export function getOwnerSystem(code: string | undefined): OwnerSystemDef | undefined {
    if (!code) return undefined;
    const normalized = code.replace(/-/g, '_');
    return OWNER_SYSTEMS.find((s) => s.code === normalized || s.code === code);
}

export function systemSectionPath(product: string, section: OwnerSystemSection): string {
    return `/control-plane/systems/${product}/${section}`;
}

export function packageTypeForSystem(code: string | undefined): PackageTypeCode {
    return getOwnerSystem(code)?.packageLine ?? 'crm';
}
